import React from "react";
import { Field, useField } from "react-final-form";
import { Box, FormHelperText, FormLabel, InputAdornment, Table, TableBody, TableContainer, TableHead, Typography } from "@mui/material";

import { CheckboxField } from "fond/form/fields";
import { NumericField } from "fond/form/fields/NumericField";
import { PRIMARY_WIRE_OPTIONS, PrimaryWireDescriptor } from "fond/project/panels/MuPower/architecture/constants";
import { atLeastOneEnabled, validateWhenEnabled } from "fond/project/panels/MuPower/architecture/helpers";
import { requireNonZero } from "fond/utils/validation";

import { TableCell, TableHeadCell, TableRow } from "./MuPowerArchitectureModal.styles";

interface PrimaryWireTableRowProps {
  option: PrimaryWireDescriptor;
  index: number;
}

const PrimaryWireTableRow: React.FC<PrimaryWireTableRowProps> = ({ option, index }: PrimaryWireTableRowProps) => {
  const {
    input: { checked: enabled },
  } = useField(`Primary.Options[${index}].Enabled`, { subscription: { value: true }, type: "checkbox" });

  return (
    <TableRow data-testid={`primary-wire-row-${option.Name}`}>
      <TableCell sx={{ pl: 1.5 }}>
        <CheckboxField
          name={`Primary.Options[${index}].Enabled`}
          size="small"
          label={
            <Box>
              <Typography variant="body2">{option.Label}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.Description}
              </Typography>
            </Box>
          }
        />
      </TableCell>
      <TableCell sx={{ px: 1.5 }}>
        <Typography variant="body2" color={enabled ? "text.primary" : "text.disabled"}>
          {option.PhaseCount}
        </Typography>
      </TableCell>
      <TableCell>
        <NumericField
          name={`Primary.Options[${index}].MaxLoadAmps`}
          disabled={!enabled}
          margin="none"
          extraValidators={[validateWhenEnabled((values) => values.Primary.Options[index].Enabled)]}
          InputProps={{ endAdornment: <InputAdornment position="end">A</InputAdornment> }}
        />
      </TableCell>
    </TableRow>
  );
};

const PrimaryWiresTab: React.FC = () => (
  <>
    <Box display="flex" gap={4}>
      <Box width={320}>
        <NumericField
          name="Primary.VoltageVolts"
          label="Primary voltage"
          required
          min={0}
          extraValidators={[requireNonZero()]}
          InputProps={{ endAdornment: <InputAdornment position="end">V</InputAdornment> }}
        />
        <Typography variant="caption" color="text.secondary" component="div">
          For all primary current (typically 14.4kV or 7.2kV)
        </Typography>
      </Box>
      <Box width={320}>
        <NumericField
          name="Primary.MaxSourceLoadAmps"
          label="Max source load"
          required
          min={0}
          extraValidators={[requireNonZero()]}
          InputProps={{ endAdornment: <InputAdornment position="end">A</InputAdornment> }}
        />
        <Typography variant="caption" color="text.secondary" component="div">
          The maximum capacity for any network source
        </Typography>
      </Box>
    </Box>
    <Box mt={2}>
      <FormLabel required>Primary wire options</FormLabel>
      <Typography variant="caption" color="text.secondary" component="div">
        Conductors the design may use on the primary network.
      </Typography>
      <TableContainer sx={{ borderRadius: 1, border: (theme) => `1px solid ${theme.palette.divider}`, my: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow className="table-head-row">
              <TableHeadCell>
                <Typography>Conductor</Typography>
              </TableHeadCell>
              <TableHeadCell width={100}>
                <Typography>Phases</Typography>
              </TableHeadCell>
              <TableHeadCell width={200}>
                <Typography>Maximum primary load</Typography>
              </TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {PRIMARY_WIRE_OPTIONS.map((option, index) => (
              <PrimaryWireTableRow key={option.Name} option={option} index={index} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Field
        name="Primary.OptionsGroup"
        validate={atLeastOneEnabled((values) => values.Primary.Options, "Enable at least one primary wire option")}
        render={({ meta }) => (meta.error && meta.submitFailed ? <FormHelperText error>{meta.error}</FormHelperText> : null)}
      />
    </Box>
  </>
);

export default PrimaryWiresTab;
